import { CheckOutlined, CloudDownloadOutlined, LoadingOutlined, SyncOutlined, SearchOutlined, BugOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Badge, Card, Spin, Input, message, Tooltip, Alert, Collapse } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useFetch } from '../../../hooks/useFetch/useFetch';
import ProjectsAddModal from '../menuAdmin/4_2_projects/ModalAddProject/ProjectsAddModal';
import TableLZM from '../../components/design/table/TableLZM';
import { ButtonLZ } from '../../components/design/Buttons/ButtonLZ';
import { customRequest } from '../../../common/request/customRequest';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { authFetch } from '../../../common/request/authFetch';
import { ErrorsDrawer } from './Modal/ErrorsDrawer';
import { BatchesModal } from './Modal/BatchesModal';
import { SERVICES_INFO } from './utils/ServicesInfo';
import { TableFilter } from '../../../common/utils/FilterUtils';

interface ServiceAlert {
  service: string;
  hoursWithoutExecution: number | null;
  limitHours: number;
}

const formatHoursWithoutExecution = (hoursValue: number | null | undefined) => {
  if (hoursValue == null || Number.isNaN(Number(hoursValue))) {
    return null;
  }
  const withoutExec = moment.duration(Number(hoursValue), 'hours');
  const days = Math.floor(withoutExec.asDays());
  const hours = withoutExec.hours();
  return days > 0 ? `${days}d ${hours}h` : `${Number(hoursValue)}h`;
};

export const Services = () => {

  const { t } = useTranslation();

  const [updateData, setUpdateData] = useState(false);
  const [serviceResponse, setServiceResponse] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSynchronizing, setIsSynchronizing] = useState(false);
  //const [openModal, setOpenModal] = useState(false);
  const [batchModal, setBatchModal] = useState(false);
  const [serviceGoogleEmail, setServiceGoogleEmail] = useState<any>(null);

  useFetch(`${process.env.REACT_APP_API}/tech/scheduledJobs`, {}, [updateData], (data) => {

    setServiceResponse(data);
    setIsLoading(isLoading);
  });

  const sync = async () => {

    setIsSynchronizing(true);
    await customRequest(`${process.env.REACT_APP_API}/tech/services`, { method: 'POST' })
      .then(() => {
        setUpdateData(!updateData);
        setIsSynchronizing(false);
      })
      .catch(() => {
        setIsSynchronizing(false);
      });
  }

  const replyReviewsAutomated = async () => {    
    await customRequest(`${process.env.REACT_APP_API}/tech/services/automated`, { method: 'POST' });
  }  

  const launchSchedulePosts = async () => {    
    await customRequest(`${process.env.REACT_APP_API}/tech/services/schedulePosts`, { method: 'POST' });
  }  

  const launchHistoricReviews = async () => {    
    await customRequest(`${process.env.REACT_APP_API}/tech/services/historicReviews`, { method: 'POST' });
  }    

  const launchBatches = async () => {    
    await customRequest(`${process.env.REACT_APP_API}/tech/services/batch/launch`, { method: 'POST' });
  }    

  const collectBatches = async () => {    
    await customRequest(`${process.env.REACT_APP_API}/tech/services/batch/collect`, { method: 'POST' });
  }    

  const groupScans = async () => {
    await customRequest(`${process.env.REACT_APP_API}/tech/services/scans/group`, { method: 'POST' });
  }    

  const labelReviews = async () => {
    await customRequest(`${process.env.REACT_APP_API}/tech/services/label`, { method: 'POST' });
  }      

  const checkObsolete = async () => {
    await customRequest(`${process.env.REACT_APP_API}/tech/services/check-obsolete`, { method: 'POST' });
  }      

  const chargeCredits = async () => {
    await customRequest(`${process.env.REACT_APP_API}/tech/services/charge-credits`, { method: 'POST' });
  }      

  const sendWeeklyReports = async () => {
    await customRequest(`${process.env.REACT_APP_API}/tech/services/reports/weekly`, { method: 'POST' });
  }

  const sendMonthlyReports = async () => {
    await customRequest(`${process.env.REACT_APP_API}/tech/services/reports/monthly`, { method: 'POST' });
  }

  const upgrade = async () => {
    await customRequest(`${process.env.REACT_APP_API}/tech/services/upgrade-data`, { method: 'POST' });
  }      
 

  /*const launchfixGroupChanges = async () => {
    await customRequest(`${process.env.REACT_APP_API}/tech/services/groupChanges`, { method: 'POST' });
  }*/    
  
  const columns = [
    {
      title: 'Project',
      key: 'project',
      filterDropdown: TableFilter,
      filterIcon: () => { return <SearchOutlined />; },
      onFilter: (value: any, record: any) => {
        return record.project.toLowerCase().includes(value.toLowerCase());
      },
      render: (text: string, record: any) => (
        <div>{record.project}</div>
      ),
    },
    {
      title: 'Servicio',
      key: 'service',
      dataIndex: 'service',
      filterDropdown: TableFilter,
      filterIcon: () => { return <SearchOutlined />; },
      onFilter: (value: any, record: any) => {
        return record.service.toLowerCase().includes(value.toLowerCase());
      },
      render: (text: string, record: any) => (
        <Tooltip title={<>                          
                          <div>{SERVICES_INFO[record.service]?.name}</div>
                          <div>{SERVICES_INFO[record.service]?.schedule}</div>
                        </>
                      }>
          <div>{record.service}</div>
        </Tooltip>
      )
    },
    {
      title: 'GBP email',
      key: 'googleEmail',
      dataIndex: 'googleEmail',
      filterDropdown: TableFilter,
      filterIcon: () => { return <SearchOutlined />; },
      onFilter: (value: any, record: any) => {
        return (record.googleEmail ?? '').toLowerCase().includes(value.toLowerCase());
      }
    },    
    {
      title: 'Info',
      key: 'message',      
      render: (text: string, record: any) => (record.message &&        
        <Tooltip title={record.message}><InfoCircleOutlined /></Tooltip>
      ),
    },
    {
      title: 'Error',
      key: 'error',
      dataIndex: 'error',
      align: 'center',   
      sorter: (a: any, b: any) => {
        if (a.error && !b.error) {
          return 1;
        } else if (!a.error && b.error) {
          return -1;
        } else {
          return 0;
        }
      },
      render: (text: string, record: any) => (
        <a style={{fontSize:'12px'}} onClick={() => {setServiceGoogleEmail({service: record.service, googleEmail: record.googleEmail})} }>
          {record.error
            ? <Badge color='red' text={<small style={{ color: '#cf1322' }}>Error</small>} />
            : <Badge color='green' text={<small style={{ color: '#389e0d' }}>Correcto</small>} />
          }
        </a>          
      ),
    },
    {
      title: 'Acción extra',
      key: 'actionRequired',
      dataIndex: 'actionRequired',
      align: 'center',
      sorter: (a: any, b: any) => Number(!!b.actionRequired) - Number(!!a.actionRequired),
      filters: [
        { text: 'Lanzada', value: true },
        { text: 'Descartada', value: false },
      ],
      onFilter: (value: any, record: any) => !!record.actionRequired === value,
      render: (value: boolean) => (
        value ? (
          <Tooltip title="Se ejecutó una acción extra: publicar programados o envío de emails por alertas">
            <Badge color='blue' text={<small style={{ color: '#2563eb' }}>Lanzada</small>} />
          </Tooltip>
        ) : (
          <Badge color='default' text={<small style={{ color: '#94a3b8' }}>Descartada</small>} />
        )
      ),
    },
    {
      title: 'Manual',
      key: 'manual',
      dataIndex: 'manual',
      render: (text: string, record: any) => (record.manual ?
        <small>Manual</small>
        :
        <small>Automated</small>
      )
    },
    {
      title: 'Última sync',
      key: 'minutesSinceSync',
      dataIndex: 'minutesSinceSync',
      sorter: (a: any, b: any) => b.minutesSinceSync - a.minutesSinceSync,
      render: (minutes: number) => {
        if (minutes == null) return '—';
        const duration = moment.duration(minutes, 'minutes');
        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const mins = duration.minutes();
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${mins}m`;
        return `${mins}m`;
      },
    },
    {
      title: 'Duración',
      key: 'duration',
      dataIndex: 'duration',
      sorter: (a: any, b: any) => b.duration - a.duration,

    }
  ]

  const notificationTest = async () => {
    await authFetch(`${process.env.REACT_APP_API}/users/notifications`, { method: 'POST' }).then((res) => {
    });
  }

  /*const infoExternalWebs = async () => {
    await authFetch(`${process.env.REACT_APP_API}/externalWebsites`, { method: 'POST' }).then((res) => {
    });
  }*/

  /*const filteredEmails = projects
    .filter((obj: { service: string; }) => obj.service === SCHEDULEDJOB.GBP_LOCATIONS)
    .map((obj: { googleEmail: any; }) => obj.googleEmail);*/

  /* Servicios */

  return (
    <>
      <Card className='main-card-content'>
        <h1 className='title-card'>{t('technology.page.services.title')}</h1>
        <div className="buttonWrapper" >
          {/*<ButtonLZ onClick={() => { setOpenModal(true) }}>lkjkjh Reseñas</ButtonLZ>*/}
          <ButtonLZ color='black' onClick={() => { upgrade() }}>UPGRADE</ButtonLZ>
          <ButtonLZ color='black' onClick={() => { setUpdateData(!updateData) }}><ReloadOutlined /></ButtonLZ>
          <ButtonLZ onClick={() => { setBatchModal(true) }}>Batches</ButtonLZ>
          <ButtonLZ color='yellow'
            onClick={() => {
              sync();
            }}>
            {!isSynchronizing ? <CloudDownloadOutlined /> : <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />} {t('technology.page.services.buttons.syncAllLocations')}
          </ButtonLZ>
          <ButtonLZ color='yellow'
            onClick={() => {
              replyReviewsAutomated();
            }}>
            {!isSynchronizing ? <CloudDownloadOutlined /> : <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />} Reseñas automated
          </ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { launchSchedulePosts() }}>Posts Programados</ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { launchHistoricReviews() }}>Reseñas Borradas</ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { launchBatches() }}>Batch: Lanzar</ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { collectBatches() }}>Batch: Resultados</ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { groupScans() }}>Scans: Group</ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { labelReviews() }}>Etiquetar</ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { checkObsolete() }}>Chequear Obsoletas</ButtonLZ>          
          <ButtonLZ color='yellow' onClick={() => { chargeCredits() }}>Recargar créditos</ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { sendWeeklyReports() }}>Informes semanal</ButtonLZ>
          <ButtonLZ color='yellow' onClick={() => { sendMonthlyReports() }}>Informes mensual</ButtonLZ>
          {/*<ButtonLZ color='yellow' onClick={() => { launchfixGroupChanges() }}>Fix Grupos</ButtonLZ> */}
          {/*<ButtonLZ onClick={() => { notificationTest() }}>Enviar Notificacion</ButtonLZ>*/}
          {/*<ButtonLZ onClick={() => { infoExternalWebs() }}>Web</ButtonLZ>*/}
        </div>

        {serviceResponse?.serviceAlerts?.length > 0 && (() => {
          const alerts: ServiceAlert[] = serviceResponse.serviceAlerts;
          const overdueCount = alerts.filter((a) => a.hoursWithoutExecution != null).length;
          const neverCount = alerts.length - overdueCount;

          return (
            <Collapse style={{ marginTop: 12 }} defaultActiveKey={[]}>
              <Collapse.Panel
                key="alerts"
                header={
                  <span>
                    <strong>Alertas de servicios</strong>
                    <span style={{ marginLeft: 10, color: '#64748b', fontWeight: 500 }}>
                      {alerts.length} total
                      {overdueCount > 0 && (
                        <span style={{ color: '#ff4d4f', marginLeft: 8 }}>
                          · {overdueCount} con retraso
                        </span>
                      )}
                      {neverCount > 0 && (
                        <span style={{ color: '#94a3b8', marginLeft: 8 }}>
                          · {neverCount} sin ejecución
                        </span>
                      )}
                    </span>
                  </span>
                }
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {alerts.map((alert) => {
                    const timeLabel = formatHoursWithoutExecution(alert.hoursWithoutExecution);
                    const isNever = timeLabel == null;

                    return (
                      <Alert
                        key={alert.service}
                        type={isNever ? 'info' : 'error'}
                        showIcon
                        style={isNever ? {
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                        } : undefined}
                        message={
                          <span style={isNever ? { color: '#64748b' } : undefined}>
                            <strong>{alert.service}</strong>
                            {' — '}
                            {isNever ? 'sin última ejecución' : `sin ejecutar ${timeLabel}`}
                            {alert.limitHours != null && (
                              <span style={{ color: '#94a3b8', marginLeft: 8 }}>
                                (límite: {alert.limitHours}h)
                              </span>
                            )}
                          </span>
                        }
                      />
                    );
                  })}
                </div>
              </Collapse.Panel>
            </Collapse>
          );
        })()}
        <TableLZM columns={columns} dataSource={serviceResponse?.scheduledJobs} rowKey={'name'} loading={isLoading} />        
      </Card>

      {/*openModal && <FixReviews updateData={updateData} setUpdateData={setUpdateData} visible={openModal} onFinish={() => { setOpenModal(false) }} emails={filteredEmails}/>*/}
      {serviceGoogleEmail && <ErrorsDrawer serviceGoogleEmail={serviceGoogleEmail} onFinish={() => {setUpdateData(!updateData)}} onClose={() => {setServiceGoogleEmail(null)}}/>}      
      {batchModal && <BatchesModal updateData={updateData} setUpdateData={setUpdateData} visible={batchModal} onFinish={() => { setBatchModal(false) }}/>}      
    </>
  )
}